/**
 * ADD AN APPOINTMENT TRIGGER — `tasks/practice-documents.md` §3.2, §24.2, §24.5.
 *
 * The third of the Documents tab's dialogs, and the last part of a document row that was still a
 * form drawn inside the list.
 *
 * ── WHY IT LEFT THE ROW ──────────────────────────────────────────────────────────────────────
 *
 * §24.5 put the trigger controls in the row on the argument that a practice reading a row asks ONE
 * question — what happens to this document — and an answer split between a panel and a dialog is
 * two answers. That argument is about the ANSWER, and the answer is still in the row: the chips,
 * their pause and their remove never moved. What moved is the four-control form for composing a
 * NEW rule, which is not an answer to anything until it is saved.
 *
 * The cost of leaving it there was paid per document rather than per use. An `action-heading`, a
 * chip field, an appointment-type picker, a `sendOn` picker, a number, a unit, a read-back
 * sentence and two buttons is most of a screen, drawn under every row in both sections, to serve
 * an action a practice takes once per document and then never again. `note-type-settings` and
 * `consent-form-settings` both keep a settings tab to a LIST of what is configured and take every
 * composition step into a dialog; this is the same move Phase 8 already made for the editor.
 *
 * ── WHAT IT OWNS, AND WHY THE WRITE IS IN HERE ───────────────────────────────────────────────
 *
 * The POST, exactly as the upload and edit dialogs own theirs. That is what lets §24.5's other
 * requirement survive the move: **a refusal must not close the form.** A dialog that handed a
 * draft back to the tab to save would have to reopen itself, repopulated, to show the refusal —
 * and a practice would be looking at a form they had already dismissed. Refusing in here leaves
 * the four controls exactly as they were typed, with the sentence underneath them.
 *
 * What the tab gets back is the upsert's response, because only the tab can act on it: the three
 * booleans decide the toast, and re-reading the link list is the tab's job since it is the tab
 * that draws the chips.
 *
 * ── ONE UNIT CROSSES THE WIRE ────────────────────────────────────────────────────────────────
 *
 * `offsetUnit` never leaves this component. §3.2 stores days, this offers weeks, and `toOffsetDays`
 * is called from exactly one place — `draftOffsetDays` — so there is a single line to look at when
 * asking what is actually sent.
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { AppointmentTypeService } from '../../../../core/services/appointment-type.service';
import { FeedbackService } from '../../../../core/services/feedback.service';
import {
  PracticeDocumentDto, PracticeDocumentLinkResponse, PracticeDocumentSendOn,
  PracticeDocumentService,
} from '../../../../core/services/practice-document.service';
import {
  PracticeDocumentOffsetUnit, SEND_ON_OPTIONS, linkRefusalMessage, offsetProblem, sendOnPhrase,
  toOffsetDays,
} from '../practice-document-copy';

export interface PracticeDocumentTriggerDialogData {
  document: PracticeDocumentDto;
}

/**
 * The upsert's answer, handed straight back.
 *
 * Not a message and not a link: the tab has to re-read its own list anyway (the POST is an upsert,
 * so what came back may be a row that was already drawn), and the three booleans are the only
 * evidence of WHICH of the three things happened. Interpreting them in here would put the sentence
 * in a dialog that has closed.
 */
export interface PracticeDocumentTriggerDialogResult {
  outcome: PracticeDocumentLinkResponse;
}

/** Said at the picker, because it is the picker that has nothing in it. */
export const APPOINTMENT_TYPES_UNREADABLE =
  'Your appointment types could not be read, so there is nothing to choose from. Close this and '
  + 'try again.';

@Component({
  selector: 'app-practice-document-trigger-dialog',
  templateUrl: './practice-document-trigger-dialog.component.html',
  styleUrls: ['./practice-document-trigger-dialog.component.scss'],
})
export class PracticeDocumentTriggerDialogComponent extends BaseComponent implements OnInit {

  readonly document: PracticeDocumentDto;

  /** The three `sendOn` values, as a practice reads them. */
  readonly sendOnOptions = SEND_ON_OPTIONS;

  readonly appointmentTypesUnreadable = APPOINTMENT_TYPES_UNREADABLE;

  /**
   * The picker's options.
   *
   * Fetched here rather than read off `AppointmentTypeService.appointmentTypes$`, which the service
   * populates from its own constructor: the subject starts `null`, and a dialog that took whatever
   * happened to be in it would show an empty picker with nothing to say for itself when that first
   * fetch had failed. This call owns its failure — `appointmentTypeError` says so, at the picker —
   * at the cost of one GET per open, which is also one GET that cannot be stale.
   */
  appointmentTypes: any[] = [];
  isLoadingAppointmentTypes = false;
  appointmentTypeError: string | null = null;

  /** THE DRAFT, IN THE PRACTICE'S UNITS. `offsetUnit` never leaves this object. */
  draft: {
    appointmentTypeId: string | null;
    sendOn: PracticeDocumentSendOn;
    offsetValue: number;
    offsetUnit: PracticeDocumentOffsetUnit;
  } = { appointmentTypeId: null, sendOn: 'confirmation', offsetValue: 1, offsetUnit: 'days' };

  isSaving = false;

  /** A refusal from the POST. Held on screen against the form, never a toast — see the header. */
  saveError: string | null = null;

  constructor(
    private practiceDocumentService: PracticeDocumentService,
    private appointmentTypeService: AppointmentTypeService,
    private dialogRef: MatDialogRef<PracticeDocumentTriggerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: PracticeDocumentTriggerDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
    this.document = data.document;
  }

  ngOnInit(): void {
    this.loadAppointmentTypes();
  }

  /**
   * The practice is the DOCUMENT's practice, not whatever is selected in the shell.
   *
   * `groupId` travels on the row itself, and it is the same id the link endpoint will check the
   * appointment type against — `NOT_A_MEMBER` is a PAIRING refusal, so a picker filled from a
   * different practice would offer nothing but rejections.
   */
  loadAppointmentTypes(): void {
    const groupId = this.document ? this.document.groupId : null;
    if (!groupId) return;

    this.isLoadingAppointmentTypes = true;
    this.appointmentTypeError = null;

    this.appointmentTypeService.getAppointmentTypesByGroup(groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoadingAppointmentTypes = false;
          this.appointmentTypes = Array.isArray(response)
            ? response
            : ((response && response.data) || []);
        },
        error: () => {
          this.isLoadingAppointmentTypes = false;
          // Emptied deliberately: a half-filled picker beside a failure notice invites a practice
          // to choose from a list that is not their list.
          this.appointmentTypes = [];
          this.appointmentTypeError = APPOINTMENT_TYPES_UNREADABLE;
        },
      });
  }

  /** §24.2: no offset exists at confirmation, so the control is not shown and cannot be typed in. */
  get draftHasOffset(): boolean {
    return this.draft.sendOn !== 'confirmation';
  }

  /**
   * THE ONE LINE THAT CROSSES THE WIRE.
   *
   * `confirmation` sends 0 whatever the draft holds — §24.2 requires it, and a form field that was
   * hidden rather than cleared is exactly how a stale 3 arrives at a column that has to be 0.
   */
  draftOffsetDays(): number {
    if (!this.draftHasOffset) return 0;
    return toOffsetDays(this.draft.offsetValue, this.draft.offsetUnit);
  }

  /**
   * The draft read back in the same words the chip will use — derived from the DAYS that will be
   * sent, never from the number in the box.
   *
   * That is the point of it. A practice typing 3 and choosing Weeks reads "3 weeks before the
   * appointment" only if `toOffsetDays` produced 21 and `sendOnPhrase` turned 21 back into three
   * weeks. A conversion that had gone wrong in either direction says so here, in front of the
   * person about to save it, rather than seven days later in somebody's inbox.
   */
  get draftPhrase(): string {
    return sendOnPhrase(this.draft.sendOn, this.draftOffsetDays());
  }

  /**
   * WHAT IS WRONG WITH THE OFFSET, as a sentence, or `null`.
   *
   * `null` at `confirmation` whatever is in the box, because at confirmation there IS no offset —
   * that is the whole of §24.2's rule, and a stale −1 left behind by someone changing their mind
   * must not be able to block a trigger that will send 0.
   *
   * It mirrors the server's `INVALID_OFFSET` rather than replacing it: refused here so the
   * practice is told at the control, refused there so a client that skipped this cannot write one.
   * The ceiling (730 days) is the server's, quoted (contract correction 6 from wave 4a).
   */
  get draftOffsetProblem(): string | null {
    if (!this.draftHasOffset) return null;
    return offsetProblem(this.draftOffsetDays());
  }

  /**
   * What may be sent. The appointment type is the only thing a practice must choose — `sendOn`
   * has a default and the offset has one too.
   */
  canSave(): boolean {
    if (!this.draft.appointmentTypeId || this.isSaving) return false;
    return this.draftOffsetProblem === null;
  }

  save(): void {
    if (!this.draft.appointmentTypeId) return;
    this.isSaving = true;
    this.saveError = null;

    this.practiceDocumentService.linkAppointmentType(this.document.id, {
      appointmentTypeId: this.draft.appointmentTypeId,
      sendOn: this.draft.sendOn,
      offsetDays: this.draftOffsetDays(),
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.dialogRef.close({ outcome: response } as PracticeDocumentTriggerDialogResult);
        },
        error: (error) => {
          this.isSaving = false;
          // THE DIALOG STAYS OPEN. A refusal the practice can act on — pick another appointment
          // type, fix the offset — is useless beside a form that has already closed.
          this.saveError =
            linkRefusalMessage(error, 'That appointment trigger could not be saved.');
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackById(index: number, item: { id: string }): string {
    return item.id;
  }
}
